import * as React from 'react';
import message from 'antd/lib/message';
import moment from 'moment';
import GenericHoc from 'src/components/hoc/GenericHoc';
import Loader from 'src/components/common/Loader';
import { BOEData } from './tracking.types';
import { boeStyles } from './tracking.styles';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { StylesProps } from 'src/Library/Types';
import { getBoeDetails } from 'src/api/shipmentExecution';
import GetExecutionData from 'src/dto/response/getExecution';

interface IProps extends StylesProps<typeof boeStyles> {
  selectedShipment: GetExecutionData;
}

const BOETracking = (props: IProps) => {
  const { classes, selectedShipment } = props;
  const [boeDetails, setBoeDetails] = React.useState<BOEData | undefined>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const loadData = async () => {
    setLoading(true);
    const response = await getBoeDetails(selectedShipment.id);
    if (response.isSuccess) {
      setBoeDetails(response?.data?.data);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (!selectedShipment.id) {
      return;
    }
    loadData();
  }, [selectedShipment.id]);

  const header = () => {
    // console.log(boeDetails);
    return <div className={classes.reference}>
      <div className={classes.referenceList}>
        <div className={classes.referenceValue}>{boeDetails?.billOfEntryNumber || '-' }</div>
        <div className={classes.referenceTitle}>BOE Number</div>
      </div>
    </div>;
  };

  const line = () => {
    return <div
      className={classes.hr}
    />;
  };

  const renderLine = () => {
    return <div className={classes.line}/>;
  };

  const renderMessage = () => {
    return <div 
      className={classes.message}>
        <div/>
        <div>
          ***All values are in INR
        </div>
    </div>;
  };

  const renderContentTitle = (title: string) => {
    return <div 
      className={classes.contentTitle}>
      {title}
    </div>;
  };

  const renderTitle = (title: string) => {
    return <div 
      className={classes.title}>
      {title}
    </div>;
  };
  const renderValue = (value: string | number | undefined, isDate: boolean = false) => {
    if (!value || value === 'N.A.') {
      return <div
        className={classes.na}>
          NA
      </div>;
    }
    if (isDate) {
      return <div
        className={classes.value}>
        {moment(value).format('DD/MM/YYYY | HH:mm A')}
      </div>;
    }
    return <div className={classes.value}>{value}</div>;
  };

  const renderField = (title: string, value: string | number | undefined, isDate: boolean = false) => {
    return <div
      className={classes.flexRow}
      // style={{ width: '23%' }}>
        style={{ width: '33%', alignItems: 'normal' }}>
      {renderTitle(title)} {renderValue(value, isDate)}
    </div>;
  };

  const renderBasicDetails = () => {
    const { billOfEntryDate } = boeDetails || {};
    const boeDate = billOfEntryDate ? moment(billOfEntryDate).format('DD/MM/YYYY') : billOfEntryDate;
    return <div className={classes.contentBox}>
      {renderContentTitle('Basic Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Location', boeDetails?.location)}
        {renderField('BoE Date', boeDate)}
      </div>
    </div>;
  };

  const renderBoEDetails = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('BoE Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('IEC', boeDetails?.billOfEntryDetails?.iec)}
        {renderField('Appraising Group', boeDetails?.billOfEntryDetails?.appraisingGroup)}
        {renderField('CHA No.', boeDetails?.billOfEntryDetails?.chaNumber)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Total Value', boeDetails?.billOfEntryDetails?.totalValue)}
        {renderField('Total Assesable', boeDetails?.billOfEntryDetails?.totalAssessableValue)}
        {renderField('Total Duty', boeDetails?.billOfEntryDetails?.totalDuty?.amount)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Total Package', boeDetails?.billOfEntryDetails?.totalPackage)}
        {renderField(
          `Gross Weight (${boeDetails?.billOfEntryDetails?.grossWeight?.unit || 'Kg'})`,
          boeDetails?.billOfEntryDetails?.grossWeight?.value,
        )}
        {renderField('First Check ', boeDetails?.billOfEntryDetails?.firstCheck)}
      </div>
      <div className={classes.contentRow}>
        {renderField('SEC 48', boeDetails?.billOfEntryDetails?.section48)}
        {renderField('WBE No.', boeDetails?.billOfEntryDetails?.wbeNumber)}
        {renderField('TYP', boeDetails?.billOfEntryDetails?.type)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Fine Penalty', boeDetails?.billOfEntryDetails?.finePenalty?.amount)}
        {renderField('Prior BE', boeDetails?.billOfEntryDetails?.priorBillOfEntry)}
      </div>
    </div>;
  };

  const renderCurrentStatus = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Current Status')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Appraisment', boeDetails?.currentStatus?.appraisement)}
        {renderField('Current Queue', boeDetails?.currentStatus?.currentQueue)}
        {renderField('No. of Query Raised', boeDetails?.currentStatus?.queryRaised)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Reply Date', boeDetails?.currentStatus?.replyDate, true)}
        {renderField('Reply Status', boeDetails?.currentStatus?.replyStatus)}
        {renderField('No. of Query Replied', boeDetails?.currentStatus?.queryReply)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Exam Date', boeDetails?.currentStatus?.examDate, true)}
        {renderField('OOC Date', boeDetails?.currentStatus?.oocDate, true)}
        {renderField('Appraisment Date', boeDetails?.currentStatus?.apprDate, true)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Payment Date', boeDetails?.currentStatus?.paymentDate, true)}
      </div>
    </div>;
  };

  const renderPaymentDetails = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Payment Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Challan No.', boeDetails?.paymentDetails?.challanNumber)}
        {renderField('Duty Amount', boeDetails?.paymentDetails?.dutyAmount?.amount)}
        {renderField('Total Duty', boeDetails?.paymentDetails?.totalDuty?.amount)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Mode', boeDetails?.paymentDetails?.modeOfPayment)}
        {renderField('Penal Amount', boeDetails?.paymentDetails?.penalAmount?.amount)}
        {renderField('Fine Amount', boeDetails?.paymentDetails?.fineAmount?.amount)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Duty Paid', boeDetails?.paymentDetails?.dutyPaid?.amount)}
        {renderField('Interest Amount', boeDetails?.paymentDetails?.interestAmount?.amount)}
      </div>
    </div>;
  };

  const renderEDocValidity = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('eDocument Validity')}
      {renderLine()}
      <div style={{ display: 'flex', marginBottom: 12 }}>
        <div className={classes.title} style={{ width: '5%' }}>S No.</div>
        <div className={classes.title} style={{ width: '15%' }}>Version</div>
        <div className={classes.title} style={{ width: '30%' }}>Description</div>
        <div className={classes.title} style={{ width: '5%' }}>Validity</div>
      </div>
      {boeDetails?.edocumentValidity?.map((item, idx) => (<div style={{ display: 'flex', marginBottom: 12 }}>
        <div className={classes.value} style={{ width: '5%' }}>{idx + 1}</div>
        <div className={classes.value} style={{ width: '15%' }}>{item?.documentVersion}</div>
        <div className={classes.value} style={{ width: '30%' }}>{item?.documentDescription}</div>
        <div className={classes.value} style={{ width: '5%' }}>{item?.validity}</div>
      </div>))}
    </div>;
  };

  const bookingOfEntry = () => {
    return <div className={classes.trackingBox}>
      {renderMessage()}
      {renderBasicDetails()}
      {renderBoEDetails()}
      {renderCurrentStatus()}
      {renderPaymentDetails()}
      {renderEDocValidity()}
    </div>;
  };

  const renderContent = () => {
    if (loading) {
      return <Loader
        style={{  marginLeft: '0px', marginTop: '25px' }}
        zIndex={1}
      />;
    }
    return <React.Fragment>
      {header()}
      {line()}
      {bookingOfEntry()}
    </React.Fragment>;
  };

  return <div className={classes.mainDiv}>
    {renderContent()}
  </div>;
};

const options: HocOptions = {
  connectJss: {
    useJss: true,
    styleSheet: boeStyles,
  },
};

const BOE = GenericHoc(options)(BOETracking);
export default BOE;
